import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { db, eq } from "../../lib/api";
import { invalidate, useDebounced, useQuery } from "../../lib/hooks";
import { setQuery, useRoute } from "../../lib/router";
import { AREA_LABEL, useRefData, type Area } from "../../lib/refdata";
import { useSession } from "../../lib/session";
import { PageHead } from "../../components/Shell";
import { Empty, ErrorBox, Field, Loading, Panel, Select, Sheet, Temperature, daysAgo, fmtShort, useToast } from "../../components/ui";
import { openPerson } from "../../components/PersonDrawer";
import { useAddToPipeline } from "../../components/OppSheet";
import { IconSearch } from "../../components/icons";
import { CardScanButton } from "../../components/CardScan";
import TravelList from "./Travel";

type View = Area | "outreach";
const VIEWS: { key: View; label: string }[] = [
  { key: "warm", label: "Warm market" }, { key: "outreach", label: "Outreach" }, { key: "networking", label: "People met" },
  { key: "hnw", label: "HNW" }, { key: "travel", label: "Travel" }, { key: "new_connections", label: "New connections" },
];

export default function People() {
  const { query } = useRoute();
  const { can } = useSession();
  const toast = useToast();
  const view = (query.get("area") as View) || "warm";
  const temp = query.get("temp") || "";
  const status = query.get("status") || "Active";
  const sort = query.get("sort") || "name";
  const page = Number(query.get("page") || 0);
  const PAGE = Number(query.get("size") || (() => { try { return localStorage.getItem("rca:pagesize:people"); } catch { return null; } })() || 50) || 2000;
  const [q, setQ] = useState(query.get("q") || "");
  const dq = useDebounced(q);
  const [adding, setAdding] = useState(false);
  const [sel, setSel] = useState<Set<string>>(new Set());
  useEffect(() => setSel(new Set()), [view]);
  const [scanned, setScanned] = useState<{ init: Record<string, string>; photo: { url: string; text: string } } | null>(null);

  const area: Area = view === "outreach" ? "warm" : view;
  const params: Record<string, string | number> = {
    select: "person_area_id,person_id,full_name,phone,email,status,bond,relationship,my_pick,outreach_since,temperature,last_touch,last_two_way,next_check_in,next_move,space_rule,rule_48h,segment,wealth_tier,came_in,platform,coverage_ends,date_met,owner_label,days_to_birthday,in_pipeline",
    area: eq(area),
    order: sort === "touch" ? "last_touch.asc.nullsfirst,full_name" : sort === "checkin" ? "next_check_in.asc.nullslast" : "full_name",
    limit: PAGE, offset: page * PAGE,
  };
  if (view === "outreach") params.outreach_since = "not.is.null";
  if (view === "warm") params.outreach_since = "is.null";   // once reignited they leave the warm market
  if (status) params.status = eq(status);
  if (temp) params.temperature = eq(temp);
  if (dq) params.or = `(full_name.ilike.*${dq.replace(/[*,()]/g, " ")}*,phone.ilike.*${dq.replace(/\D/g, "") || "~"}*,email.ilike.*${dq.replace(/[*,()]/g, " ")}*)`;

  const list = useQuery(`people:${JSON.stringify(params)}`, () => db.selectCount("person_area_stats", params));
  const counts = useQuery("people:counts", async () => {
    const r = await Promise.all(VIEWS.map((v) =>
      db.selectCount("person_area_stats", { select: "person_area_id", area: eq(v.key === "outreach" ? "warm" : v.key), status: v.key === "travel" ? 'in.(Active,"To prospect",Nurture)' : eq("Active"), limit: 1, ...(v.key === "outreach" ? { outreach_since: "not.is.null" } : v.key === "warm" ? { outreach_since: "is.null" } : {}) })));
    return Object.fromEntries(VIEWS.map((v, i) => [v.key, r[i].total]));
  });

  async function togglePick(row: any) {
    await db.update("person_areas", { id: eq(row.person_area_id) }, { my_pick: !row.my_pick });
    invalidate("people"); invalidate("today");
  }
  async function toOutreach(row: any) {
    await db.update("person_areas", { id: eq(row.person_area_id) }, { outreach_since: dayjs().format("YYYY-MM-DD") });
    invalidate("people"); invalidate("today");
    toast(`${row.full_name} moved to Outreach and out of the warm market`); invalidate("dash:");
  }

  const addToPipeline = useAddToPipeline();
  const canPipe = can("pipelines", "edit");
  const pipeBtn = (r: any) => canPipe && !r.in_pipeline && (
    <button className="btn small ghost" onClick={(e) => { e.stopPropagation(); addToPipeline({ person_id: r.person_id, full_name: r.full_name, source_area: r.area }); }}>+ Pipeline</button>);
  const rows = list.data?.rows ?? [];
  const ids = rows.map((r: any) => r.person_id);
  const known = useQuery(ids.length ? `people:known:${ids.join(",").slice(0, 2000)}:${ids.length}` : null, () =>
    db.select<{ id: string; known_as: string | null }>("people", { select: "id,known_as", id: `in.(${ids.join(",")})`, known_as: "not.is.null" }));
  const knownOf = (id: string) => known.data?.find((k) => k.id === id)?.known_as;
  const total = list.data?.total ?? 0;
  const editable = can("leads", "edit");

  return (
    <>
      <PageHead help="lists" title="People" sub="Everyone you're building a relationship with, by area."
        actions={editable && <><CardScanButton onRead={(init, photo) => setScanned({ init, photo })} /><button className="btn primary" onClick={() => setAdding(true)}>Add person</button></>} />
      <div className="toolbar">
        <div className="seg" role="tablist">
          {VIEWS.map((v) => (
            <button key={v.key} aria-pressed={view === v.key} onClick={() => setQuery({ area: v.key, page: null, temp: null })}>
              {v.label}{counts.data?.[v.key] != null && <span className="n">({counts.data[v.key]})</span>}
            </button>
          ))}
        </div>
      </div>
      {view !== "travel" && <div className="toolbar">
        <div className="search grow">
          <IconSearch size={16} />
          <input className="input" value={q} onChange={(e) => { setQ(e.target.value); setQuery({ q: e.target.value || null, page: null }); }} placeholder="Search name, phone or email" />
        </div>
        {view !== "warm" && (
          <Select value={temp} onChange={(v) => setQuery({ temp: v || null, page: null })} options={["Warm", "Cooling", "Cold"]} placeholder="Any temperature" style={{ width: 170 }} />
        )}
        <Select value={status} onChange={(v) => setQuery({ status: v || "", page: null })} options={["Active", "To prospect", "Nurture", "Not now", "Do not contact", "Not a fit", "Not interested", "Archived"]} placeholder="Any status" style={{ width: 160 }} />
        <Select value={sort} onChange={(v) => setQuery({ sort: v || null })} options={[{ value: "name", label: "Sort: name" }, { value: "touch", label: "Sort: longest since touch" }, { value: "checkin", label: "Sort: next check-in" }]} style={{ width: 210 }} />
      </div>}
      {view === "travel" ? <TravelList /> : <>
      <ErrorBox error={list.error} />
      <Panel>
        {editable && sel.size > 0 && <BulkBar ids={[...sel]} from={area} onDone={() => setSel(new Set())} />}
        {list.loading && !list.data ? <Loading rows={8} /> : rows.length === 0 ? (
          <Empty title={dq ? `No one matches “${dq}”` : `No one in ${VIEWS.find((v) => v.key === view)?.label} yet`}>
            {editable && !dq && <>Use <b style={{ display: "inline" }}>Add person</b> to start the list.</>}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  {editable && <th style={{ width: 32 }} onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label="Select all on this page"
                    checked={rows.length > 0 && rows.every((r: any) => sel.has(r.person_id))}
                    onChange={(e) => setSel((x) => { const n = new Set(x); rows.forEach((r: any) => (e.target.checked ? n.add(r.person_id) : n.delete(r.person_id))); return n; })} /></th>}
                  <th>Name</th>
                  {view === "warm" && <><th>Relationship</th><th>Owner</th><th>Last touch</th><th>My pick</th><th /></>}
                  {view !== "warm" && <><th>Temperature</th><th>Last touch</th><th>Next check-in</th><th>Next move</th></>}
                  {view === "networking" && <th>48-hour rule</th>}
                  {view === "hnw" && <th>Segment</th>}
                  {view === "new_connections" && <th>Came in</th>}
                  {view !== "warm" && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r: any) => (
                  <tr key={r.person_area_id} onClick={() => openPerson(r.person_id)}>
                    {editable && <td onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label={`Select ${r.full_name}`} checked={sel.has(r.person_id)}
                      onChange={() => setSel((x) => { const n = new Set(x); n.has(r.person_id) ? n.delete(r.person_id) : n.add(r.person_id); return n; })} /></td>}
                    <td>
                      <strong>{r.full_name}</strong>{knownOf(r.person_id) && <span className="known-as"> · {knownOf(r.person_id)}</span>}
                      {r.days_to_birthday != null && r.days_to_birthday <= 7 && <span className="pill special" style={{ marginLeft: 6 }}>Birthday {r.days_to_birthday === 0 ? "today" : `in ${r.days_to_birthday}d`}</span>}
                      {r.in_pipeline && <span className="pill leads" style={{ marginLeft: 6 }}>In pipeline</span>}
                      <div className="small muted">{r.phone ?? r.email ?? ""}</div>
                    </td>
                    {view === "warm" && <>
                      <td>{r.relationship}</td>
                      <td className="small">{r.owner_label}</td>
                      <td className="nowrap small">{daysAgo(r.last_touch)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label="My pick" checked={!!r.my_pick} disabled={!editable} onChange={() => togglePick(r)} />
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {!r.outreach_since && editable && <button className="btn small ghost" onClick={() => toOutreach(r)}>Move to Outreach</button>}
                        {r.outreach_since && <span className="pill">Outreach</span>}
                        {pipeBtn(r)}
                      </td>
                    </>}
                    {view !== "warm" && <>
                      <td><Temperature t={r.temperature} /></td>
                      <td className="nowrap small">{daysAgo(r.last_touch)}</td>
                      <td className="nowrap small">{r.next_check_in ? fmtShort(r.next_check_in) : "—"}</td>
                      <td className="small">{r.in_pipeline ? "In pipeline: follow the deal" : r.space_rule !== "OK" ? r.space_rule : r.next_move}</td>
                    </>}
                    {view === "networking" && <td><span className={`pill ${r.rule_48h === "Within 48h" ? "warm" : r.rule_48h === "Late" || r.rule_48h === "Not messaged" ? "alert" : ""}`}>{r.rule_48h ?? "—"}</span></td>}
                    {view === "hnw" && <td className="small">{r.segment}</td>}
                    {view === "new_connections" && <td className="small">{r.came_in} · {r.platform}</td>}
                    {view !== "warm" && <td onClick={(e) => e.stopPropagation()}>{pipeBtn(r)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {total > 0 && (
          <div className="pager">
            <span className="pg-info">{page * PAGE + 1}–{Math.min(total, (page + 1) * PAGE)} of {total} people</span>
            <label className="pg-size">Show
              <select className="select" value={PAGE >= 2000 ? 0 : PAGE} onChange={(e) => { const v = e.target.value; try { localStorage.setItem("rca:pagesize:people", v === "0" ? "2000" : v); } catch { /* */ } setQuery({ size: v === "0" ? "2000" : v, page: null }); }}>
                {[10, 20, 25, 50, 100, 0].map((n) => <option key={n} value={n}>{n === 0 ? "All" : n}</option>)}
              </select>
            </label>
            {total > PAGE && (
              <div className="pg-nav">
                <button className="icon-btn" disabled={page === 0} onClick={() => setQuery({ page: String(page - 1) })} aria-label="Previous page">‹</button>
                <span className="small muted" style={{ padding: "0 6px" }}>Page {page + 1} of {Math.ceil(total / PAGE)}</span>
                <button className="icon-btn" disabled={(page + 1) * PAGE >= total} onClick={() => setQuery({ page: String(page + 1) })} aria-label="Next page">›</button>
              </div>
            )}
          </div>
        )}
      </Panel>
      </>}
      {adding && <AddPerson area={area} outreach={view === "outreach"} onClose={() => setAdding(false)} />}
      {scanned && <AddPerson area="networking" outreach={false} initial={scanned.init} photo={scanned.photo} onClose={() => setScanned(null)} />}
    </>
  );
}

/** Act on the ticked people: move them to another list, or tag them. */
function BulkBar({ ids, from, onDone }: { ids: string[]; from: Area; onDone: () => void }) {
  const toast = useToast();
  const tags = useQuery("tags:all", () => db.select<{ id: string; name: string }>("tags", { select: "id,name", order: "name" }));
  const [err, setErr] = useState("");
  async function move(to: string) {
    if (!to) return;
    try {
      const r = await db.rpc<{ moved: number }>("people_move_list", { p_people: ids, p_to: to, p_from: from });
      invalidate("people"); invalidate("today"); invalidate("person:"); toast(`${r.moved} moved to ${AREA_LABEL[to as Area]}`); onDone();
    } catch (e) { setErr((e as Error).message); }
  }
  async function tag(tagId: string) {
    if (!tagId) return;
    try {
      await db.upsert("person_tags", ids.map((person_id) => ({ person_id, tag_id: tagId })), "person_id,tag_id");
      invalidate("person:"); toast(`Tagged ${ids.length} ${ids.length === 1 ? "person" : "people"}`); onDone();
    } catch (e) { setErr((e as Error).message); }
  }
  return (
    <div className="bulkbar">
      <b>{ids.length} selected</b>
      <Select value="" onChange={move} placeholder="Move to list…" style={{ width: 180 }}
        options={(["warm", "networking", "hnw", "travel", "new_connections"] as Area[]).filter((a) => a !== from).map((a) => ({ value: a, label: AREA_LABEL[a] }))} />
      <Select value="" onChange={tag} placeholder="Add tag…" style={{ width: 160 }} options={(tags.data ?? []).map((t) => ({ value: t.id, label: t.name }))} />
      <button className="btn small ghost" onClick={onDone}>Clear</button>
      {err && <span className="small" style={{ color: "var(--alert)" }}>{err}</span>}
    </div>
  );
}

export function AddPerson({ area, outreach, onClose, initial, photo, onSaved }: { area: Area; outreach: boolean; onClose: () => void; initial?: Record<string, string>; photo?: { url: string; text: string }; onSaved?: (id: string) => void }) {
  const ref = useRefData();
  const toast = useToast();
  const [a, setA] = useState<Area>(area);
  const [f, setF] = useState<Record<string, string>>(initial ?? {});
  const scanned = !!photo;
  const [error, setError] = useState<Error | null>(null);
  const [dupes, setDupes] = useState<{ id: string; full_name: string }[] | null>(null);
  const set = (k: string) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  const inp = (k: string, type = "text") => <input className="input" type={type} value={f[k] ?? ""} onChange={(e) => set(k)(e.target.value)} />;

  async function save(force = false) {
    setError(null);
    try {
      const name = `${f.first_name ?? ""} ${f.last_name ?? ""}`.trim();
      if (!name) throw new Error("Add at least a first name.");
      if (!force) {
        const or = [f.email && `email.eq.${f.email}`, f.phone && `phone_digits.like.*${f.phone.replace(/\D/g, "").slice(-10)}`, `full_name.ilike.${name}`].filter(Boolean).join(",");
        const found = await db.select<{ id: string; full_name: string }>("people", { select: "id,full_name", or: `(${or})`, limit: 5 });
        if (found.length) { setDupes(found); return; }
      }
      const [p] = await db.insert<{ id: string }>("people", {
        first_name: f.first_name, last_name: f.last_name ?? "", phone: f.phone || null, email: f.email || null,
        occupation: f.occupation || null, company: f.company || null, linkedin: f.linkedin || null, instagram: f.instagram || null,
        family_note: f.family_note || null, interests: f.interests || null, notes: f.notes || null, source: f.source || null, gender: f.gender || null, known_as: f.known_as || null,
      });
      await addArea(p.id);
      invalidate("people"); invalidate("today");
      toast(`Added ${name}`);
      if (onSaved) return onSaved(p.id);
      onClose();
      openPerson(p.id);
    } catch (e) { setError(e as Error); }
  }
  async function addArea(personId: string) {
    await db.insert("person_areas", {
      person_id: personId, area: a, status: "Active", bond: f.bond || null, relationship: f.relationship || null,
      outreach_since: outreach && a === "warm" ? dayjs().format("YYYY-MM-DD") : null,
      date_met: f.date_met || null, how_i_can_help: f.how_i_can_help || null, segment: f.segment || null,
      wealth_tier: f.wealth_tier || null, main_channel: f.main_channel || null, found_where: f.found_where || null,
      research_note: f.research_note || null, client_type: f.client_type || null, coverage_ends: f.coverage_ends || null,
      came_in: f.came_in || null, platform: f.platform || null, what_caught: f.what_caught || null,
    });
    if (f.event_id) await db.insert("event_people", { event_id: f.event_id, person_id: personId, first_met: true });
  }
  const events = useQuery(a === "networking" ? "events:list" : null, () => db.select<{ id: string; name: string }>("events", { select: "id,name", order: "event_date.desc.nullslast" }));

  if (dupes) {
    return (
      <Sheet title="Already in your CRM?" onClose={onClose} footer={<>
        <button className="btn ghost" onClick={() => { setDupes(null); save(true); }}>No, add as a new person</button>
      </>}>
        <p className="small">These people have the same name, phone or email. Add this area to one of them instead of creating a duplicate:</p>
        {dupes.map((d) => (
          <button key={d.id} className="btn" style={{ justifyContent: "space-between" }} onClick={async () => {
            try { await addArea(d.id); invalidate("people"); invalidate(`person:${d.id}`); toast(`Added ${d.full_name} to ${AREA_LABEL[a]}`); if (onSaved) return onSaved(d.id); onClose(); openPerson(d.id); }
            catch (e) { setError(e as Error); setDupes(null); }
          }}>{d.full_name}<span className="small muted">Use this person</span></button>
        ))}
      </Sheet>
    );
  }

  return (
    <Sheet title={scanned ? "Check the card details" : "Add person"} onClose={onClose} footer={<button className="btn primary" onClick={() => save()}>Add person</button>}>
      <ErrorBox error={error} />
      {photo && <div className="card-scan-review">
        <img src={photo.url} alt="The card you scanned" />
        <div className="small muted">Check each field against the photo. The reader can mix up letters on shiny or fancy cards.
          <details><summary>What the card says</summary><pre>{photo.text.trim() || "(no text found)"}</pre></details></div>
      </div>}
      <Field label="Area">
        <Select value={a} onChange={(v) => setA((v || "warm") as Area)} options={(["warm", "networking", "hnw", "travel", "new_connections"] as Area[]).map((x) => ({ value: x, label: AREA_LABEL[x] }))} />
      </Field>
      <div className="form-grid">
        <Field label="First name">{inp("first_name")}</Field>
        <Field label="Last name">{inp("last_name")}</Field>
        <Field label="Gender"><Select value={f.gender} onChange={set("gender")} options={ref.data?.list("Gender") ?? []} /></Field>
        <Field label="Known as (to tell them apart)">{inp("known_as")}</Field>
        <Field label="Phone">{inp("phone", "tel")}</Field>
        <Field label="Email">{inp("email", "email")}</Field>
        {scanned && <>
          <Field label="Company">{inp("company")}</Field>
          <Field label="Job title">{inp("occupation")}</Field>
          <Field label="Notes" wide>{inp("notes")}</Field>
        </>}
        {a === "warm" && <>
          <Field label="Relationship"><Select value={f.relationship} onChange={set("relationship")} options={ref.data?.list("WarmRelationship") ?? []} /></Field>
          <Field label="Source"><Select value={f.source} onChange={set("source")} options={ref.data?.list("Sources") ?? []} /></Field>
        </>}
        {(a === "networking" || outreach) && <Field label="Bond"><Select value={f.bond} onChange={set("bond")} options={ref.data?.list("Bonds") ?? []} /></Field>}
        {a === "networking" && <>
          <Field label="Date met">{inp("date_met", "date")}</Field>
          <Field label="Event"><Select value={f.event_id} onChange={set("event_id")} options={(events.data ?? []).map((e) => ({ value: e.id, label: e.name }))} /></Field>
          {!scanned && <Field label="Work / business">{inp("company")}</Field>}
          <Field label="How I can help them" wide>{inp("how_i_can_help")}</Field>
        </>}
        {a === "hnw" && <>
          <Field label="Segment"><Select value={f.segment} onChange={set("segment")} options={ref.data?.list("Segments") ?? []} /></Field>
          <Field label="Wealth tier"><Select value={f.wealth_tier} onChange={set("wealth_tier")} options={ref.data?.list("WealthTiers") ?? []} /></Field>
          <Field label="Main channel"><Select value={f.main_channel} onChange={set("main_channel")} options={ref.data?.list("Channels") ?? []} /></Field>
          <Field label="Where I found them"><Select value={f.found_where} onChange={set("found_where")} options={ref.data?.list("FoundWhere") ?? []} /></Field>
          <Field label="Research note" wide>{inp("research_note")}</Field>
        </>}
        {a === "travel" && <>
          <Field label="Client type"><Select value={f.client_type} onChange={set("client_type")} options={ref.data?.list("ClientType") ?? []} /></Field>
          <Field label="Coverage ends">{inp("coverage_ends", "date")}</Field>
        </>}
        {a === "new_connections" && <>
          <Field label="Came in"><Select value={f.came_in} onChange={set("came_in")} options={ref.data?.list("Inbound") ?? []} /></Field>
          <Field label="Platform"><Select value={f.platform} onChange={set("platform")} options={ref.data?.list("Channels") ?? []} /></Field>
          <Field label="What caught my attention" wide>{inp("what_caught")}</Field>
        </>}
        <Field label="Family" wide>{inp("family_note")}</Field>
        <Field label="Interests" wide>{inp("interests")}</Field>
      </div>
    </Sheet>
  );
}
